import { TaskAssignmentGet, Ulid } from "@kase/my-marathon-communication";
import { ReadonlySignal, computed } from "@preact/signals";
import { uniqByFn } from "~/utils";
import { createListController, createSubscribe } from "../utils";
import {
    AssignmentSliceParams,
    CreateTaskAssignmentRequest,
    FilterTaskAssignment,
    TaskAssignment,
} from "./interfaces";

export const makeAssignment = ({
    connection,
    loadTaskUser,
}: AssignmentSliceParams) => {
    const taskStore: Record<string, ReadonlySignal<TaskAssignment[]>> = {};

    const {
        items: assignments,
        allLoading: loading,
        loading: loadingBy,
        listen,
    } = createListController<
        TaskAssignmentGet,
        TaskAssignment,
        FilterTaskAssignment
    >({
        connection,
        list: (con, { task, assignee }) =>
            con.task.assignment.items({ task, assignee }),
        getKey: ({ task, assignee }) =>
            `${task?.toString() ?? "0"}_${assignee?.toString() ?? "0"}`,
        processBatch: (batch) => {
            const taskUserIds = uniqByFn(
                batch.map((item) => item.task_user),
                (a, b) => a.equals(b),
            );

            loadTaskUser(taskUserIds);

            return batch.map((item) => ({
                ...item,
                id: item.id.toString(),
                _id: item.id,
            }));
        },
        name: "taskassignment",
    });

    const loadAssignment = async (
        id: Readonly<Ulid> | readonly Readonly<Ulid>[],
    ) => {
        const con = connection.value;
        const finalId = Array.isArray(id) ? id : [id];
        const items = con.task.assignment.get({ id: finalId });
        const result: TaskAssignment[] = [];

        for await (const item of items) {
            const finalAssignment = {
                ...item,
                id: item.id.toString(),
                _id: item.id,
            };

            assignments.value = [...assignments.peek(), finalAssignment];
            result.push(finalAssignment);
        }

        for (const assignment of result) {
            loadTaskUser(assignment.task_user);
        }

        return result;
    };

    const listenTasks = (taskIds: Readonly<Ulid>[]) =>
        taskIds.forEach((task) => listen({ task }));

    const listenAssignees = (assigneeIds: Readonly<Ulid>[]) =>
        assigneeIds.forEach((assignee) => listen({ assignee }));

    const create = async (request: CreateTaskAssignmentRequest) => {
        const con = connection.value;

        if (!con) {
            throw new Error("Connection is not available or task not found");
        }

        try {
            await con.task.assignment.create({
                assignee: request.assignee,
                task: request.task,
                start: request.start,
                daymax: request.daymax,
                planned: request.planned,
            });
        } catch (error) {
            console.error(error);
        }
    };

    const task = (taskId: Readonly<Ulid>) => {
        const key = taskId.toString();

        if (!(key in taskStore)) {
            taskStore[key] = computed(() =>
                assignments.value.filter((item) => item.task.equals(taskId)),
            );

            listen({ task: taskId });
        }

        return taskStore[taskId.toString()];
    };

    const subscribe = createSubscribe(
        assignments,
        connection,
        (con) => con.task.assignment.subscribe(),
        loadAssignment,
    );

    return {
        assignments,
        loading,
        listenTasks,
        listenAssignees,
        create,
        task,
        taskLoading: (task: Readonly<Ulid>) => loadingBy({ task }),
        subscribe,
    };
};
